import { useCallback, useEffect, useState } from 'react';

import { HiPencil as EditIcon } from 'react-icons/hi';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import classnames from 'classnames';
import { analyzeImage } from 'general/helpers';
import { mainMenuCategories } from 'general/links';
import { useAppSelector } from 'redux/store/store.types';

import { useEecsCompany } from 'modules/eecs-go';
import { INavigationLink, usePlatform } from 'modules/platform';
import { useUsers } from 'modules/users';

import { CircularProgress, List } from '@material-ui/core';
import { NotificationsNone } from '@material-ui/icons';

import NibButton from 'components/common/NibButton/NibButton';
import NibDialog from 'components/common/NibDialog/NibDialog';
import NibSelectWithSearch from 'components/common/NibFilters/NibSelectWithSearch';

import SideBarItem from './SideBarItem';
import css from './appNav.module.sass';

const SideBar = ({ open, getAvatarSrc, userRolesText }: any) => {
  const history = useHistory();
  const location = useLocation();

  const rolesDisabled = !!Number(process.env.REACT_APP_DISABLE_ROLES);

  const { fetchFeatureSettingsHandler } = usePlatform();
  const { searchEecsCompanyQueryHandler } = useEecsCompany();
  const { switchUserCompanyHandler } = useUsers();

  const { currentUser } = useAppSelector((store) => store.authSession);
  const { hasUnreadMessages } = useAppSelector((store) => store.appChat);
  const { navigationLinks } = useAppSelector((store) => store.platform);

  const [activePage, setActive] = useState('/');
  const [bgColor, setBgColor] = useState('black');
  const [openCompanySwitchModal, setOpenCompanySwitchModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<undefined | number>(undefined);

  const haveRights = (nl: INavigationLink) => {
    for (let role of currentUser?.roles || []) {
      if (nl.roles_allowed && nl.roles_allowed.includes(role.name)) return true;
    }
    return false;
  };

  const menuCategoryLinks = mainMenuCategories
    .map((category) => {
      return {
        name: category.name,
        links: category.links
          .map((link) => {
            const filteredNavLinks = navigationLinks.filter((nl) => nl.alwaysHide !== true && nl.disabled !== true);
            for (let nl of filteredNavLinks) {
              if (link === nl.to) {
                if ((!nl.hide && (currentUser?.hasAdminAccess || rolesDisabled)) || haveRights(nl)) {
                  return nl;
                }
              }
            }
            return null;
          })
          .filter((nl) => nl !== null),
      };
    })
    .filter((category) => category.links.length > 0);

  const isActivePage = useCallback(
    (page: string) => {
      const path = activePage.split('/');
      if (path.length > 1) {
        return path[1].includes(page.replace('/', ''));
      }
      return false;
    },
    [activePage],
  );
  const switchCompanyHandler = useCallback(async () => {
    if (!currentUser || !selectedCompany) return;
    setIsLoading(true);
    const result = await switchUserCompanyHandler({
      user_id: currentUser.id,
      company_id: selectedCompany,
    });
    if (result) {
      window.location.reload();
      toast.success('Company Switched!');
      setOpenCompanySwitchModal(false);
    }
    setIsLoading(false);
  }, [isLoading, selectedCompany, currentUser]);

  const fetchSettingsHandler = async () => {
    await fetchFeatureSettingsHandler();
    setIsSettingsLoading(false);
  };

  useEffect(() => {
    if (location.pathname !== activePage) {
      setActive(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    if (currentUser) {
      fetchSettingsHandler();
    }
  }, [currentUser]);

  return (
    <>
      <NibDialog open={openCompanySwitchModal} papperClass="!bg-appDark80" title="Switch Company" width={400}>
        {/* @ts-ignore */}
        <div className="w-full">
          <div className="ui-table-filter !mt-0 w-full !flex">
            <NibSelectWithSearch
              inputClassName="!h-[54px]"
              title="Select New Company"
              // @ts-ignore
              value={selectedCompany}
              alias="necsCompanyId"
              // @ts-ignore
              onFilter={(_alias: string, value: number | null) => setSelectedCompany(value || undefined)}
              // @ts-ignore
              fetchOptions={(search) =>
                searchEecsCompanyQueryHandler({ search, registers: null }).then((data) =>
                  data.map((company) => ({ ...company })),
                )
              }
              // @ts-ignore
              useFetchDispatch={false}
              withDispatch={false}
            />
          </div>
          <div className="flex justify-end items-center w-full gap-3 pt-2">
            {/* @ts-ignore */}
            <NibButton
              label="Cancel"
              disabled={isLoading}
              mui={true}
              onClick={() => setOpenCompanySwitchModal(false)}
            />
            {/* @ts-ignore */}
            <NibButton
              label="Apply"
              disabled={isLoading}
              loading={isLoading}
              mui={true}
              onClick={() => switchCompanyHandler()}
            />
          </div>
        </div>
      </NibDialog>

      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        className={classnames(css.sideBar, open && css.open, 'bg-appDark160')}
      >
        <div className="flex min-h-[100px]">
          <div className="rounded-[50%] mr-[42px]">
            <div
              className="block w-[70px] h-[70px] bg-white rounded-[50%] overflow-hidden bg-center bg-cover"
              style={{ backgroundColor: bgColor }}
            >
              <img
                src={getAvatarSrc()}
                alt="profile_logo"
                crossOrigin="anonymous"
                onLoad={(e) => {
                  const brightness = analyzeImage(e.target);
                  brightness !== null && setBgColor(brightness < 90 ? 'black' : '#D3D3D3');
                }}
                className="w-full h-full object-contain"
              />
            </div>
            <div
              className="bg-white cursor-pointer w-[30px] flex items-center justify-center h-[30px] rounded-[50px] absolute top-[45px] left-[80px]"
              onClick={() => history.push('/user-profile')}
            >
              {/* @ts-ignore */}
              <EditIcon className="text-black" size={18} />
            </div>
          </div>
          <div className="font-semibold text-[20px]">
            <div>Hi, {currentUser?.fullName}</div>
            <div
              onClick={() => {
                if (currentUser?.hasAdminAccess) {
                  setOpenCompanySwitchModal(true);
                }
              }}
              className={`pb-2 text-xs ${currentUser?.hasAdminAccess && 'underline decoration-dotted cursor-pointer'}`}
            >
              {currentUser?.company?.name}
            </div>
            <div className="text-xs bg-appThemeColor text-appThemeTextColor rounded-lg float-left px-[10px]">
              {userRolesText}
            </div>
          </div>
        </div>

        <div style={{ width: '100%', height: '100%' }} className={css.DesktopMenuScrollbars}>
          {isSettingsLoading ? (
            <div className="w-full h-full flex items-center justify-center pt-4">
              <CircularProgress />
            </div>
          ) : (
            <div>
              {menuCategoryLinks.map((menuCategory) => (
                <div key={menuCategory.name} className="w-full">
                  <span className="font-semibold text-[18px] border-b border-white/15 w-full flex">
                    {menuCategory.name}
                  </span>
                  <div>
                    {menuCategory.links.map((nl) => {
                      if (!nl) return null;
                      let IconComponent = null;
                      if (nl.label === 'Communication' && hasUnreadMessages) {
                        IconComponent = NotificationsNone;
                      }

                      return (
                        // @ts-ignore
                        <SideBarItem
                          key={nl.to}
                          className={isActivePage(nl.to) && css.active}
                          active={isActivePage(nl.to)}
                          location={location}
                          onClick={() => setActive(nl.to)}
                          {...{ ...nl, IconComponent }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </List>
    </>
  );
};

export default SideBar;
